import { useCallback, useEffect } from "react";
import { useFlow } from "@/contexts/FlowContext";
import Button from "@scripts/react/components/Button";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { SidebarActions } from "@/components/layout/SidebarActions";
import { BuildSteps, Flows } from "@/types/flows";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { DEFAULT_DURATION } from "@/constants/motion";
import { TestWithMoreFilesActionCard } from "./TestWithMoreFilesActionCard";
import { useModel } from "@/contexts/ModelContext";
import { useGetPredictions } from "@/hooks/useGetPredictions";
import { useFlowState } from "@/hooks/useFlowState";
import { PublishActionButton } from "./PublishActionButton";
import { AdjustPredictionsActionCard } from "./AdjustPredictionsActionCard";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { usePageNavigationShortcut } from "@/hooks/usePageNavigationShortcut";
import { useStopGeneratingPredictions } from "@/hooks/useStopGeneratingPredictions";
import { useClearAllPredictions } from "@/store";

const ButtonComponent = Button as any;

interface ReviewActionButtonsProps {
    forceVideoFlow?: boolean;
}

export const ReviewActionButtons = ({ forceVideoFlow = false }: ReviewActionButtonsProps) => {
    // Use legacy hook for data access and optimized hooks for other features
    const { annotationsForFileAndFrameId, task } = useFlow();
    const { setStep } = useFlowState();
    const { isDownloading } = useModel();
    const clearAllPredictions = useClearAllPredictions();

    // Analytics
    const { track } = useAnalytics();

    const { stopGeneratingPredictions } = useStopGeneratingPredictions();
    const handleConfirmRelabel = useCallback(() => {
        stopGeneratingPredictions();
        setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
        track({
            event: AnalyticsFlowEvent.REPROMPT_CLICKED,
            data: {
                task,
                prompted_files_count: Object.keys(annotationsForFileAndFrameId).length,
                prompts_count: Object.values(annotationsForFileAndFrameId).reduce(
                    (acc: number, annotationsForFile: any) =>
                        acc + Object.values(annotationsForFile).length,
                    0
                )
            }
        });
        // Clear predictions for all upload intents
        clearAllPredictions();
    }, [
        annotationsForFileAndFrameId,
        setStep,
        track,
        task,
        stopGeneratingPredictions,
        clearAllPredictions
    ]);

    // UGLY hack, but if there is an error in model generation, we switch back to the prompt step where the error is displayed
    // TODO(japrescott, 2025-09-10): Better error handling not relying on the inference queue state
    const { isLoadingInferenceQueue, errorInferenceQueue } = useGetPredictions();
    useEffect(() => {
        if (!isLoadingInferenceQueue && errorInferenceQueue) {
            console.log("WOOOPS, got an error on the inference queue, going back to prompt step", {
                errorInferenceQueue
            });
            setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
        }
    }, [errorInferenceQueue, isLoadingInferenceQueue, setStep]);

    // Keyboard shortcut: Command + Left Arrow
    usePageNavigationShortcut("left", handleConfirmRelabel, isDownloading);

    return (
        <>
            <div className="content gap-3">
                <TestWithMoreFilesActionCard />
                <AdjustPredictionsActionCard />
            </div>

            {/* Desktop CTAs */}
            <SidebarActions
                toolbarProps={{
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    transition: { duration: DEFAULT_DURATION.slower, ease: "easeOut" }
                }}
            >
                <Button type="secondary" onClick={handleConfirmRelabel} disabled={isDownloading}>
                    <i className="far fa-arrow-left"></i>
                    <span>Relabel Objects</span>
                    <div
                        className={twClassNames("hotkeysContainer", isDownloading && "opacity-30")}
                    >
                        <span className="hotkey">⌘</span>
                        <span className="hotkey">←</span>
                    </div>
                </Button>
                <PublishActionButton />
            </SidebarActions>
        </>
    );
};
